import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client'
import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import { ContentItem } from '@deltek/specification-client/core/Models/contentItem'
import { ProjectElementType } from '@deltek/specification-client/core/Models/projectElement'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Button, CardContent, makeStyles, Theme } from '@material-ui/core'
import Card from '@material-ui/core/Card'
import IconButton from '@material-ui/core/IconButton'
import CheckIcon from '@material-ui/icons/Check'
import CloseIcon from '@material-ui/icons/Close'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import {
    AcceptTrackedChange,
    DeleteTrackTipComment,
    RejectTrackedChange,
} from 'api/graphQL/mutations/change.mutations'
import {
    GetProjectNuggetContentItemNodes,
    GetProjectProductFamilyContentItemNodes,
} from 'api/graphQL/queries/projectElements.queries'
import {
    ElementType,
    setElementTrackedChanges,
    setTrackChangeComment,
    workspaceSelectedElement,
} from 'api/graphQL/reactiveVars/element'
import {
    resetOnFocusProperty,
    setArticleToFocus,
    setOptionGroupToFocus,
    setPropertyToFocus,
    setTabToFocus,
    workspaceProperty,
} from 'api/graphQL/reactiveVars/property'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import {
    setWorkspaceContentItems,
    workspaceContents,
} from 'api/graphQL/reactiveVars/workspaceContent'
import {
    GetProjectNuggetResponse,
    GetProjectProductFamilyResponse,
} from 'api/graphQL/types/projectElement.types'
import clsx from 'clsx'
import {
    DeleteTrackChangeCommentInput,
    TrackChangeStatus,
    TrackChangeType,
    TrackedChange,
} from 'models/changes'
import { ProductFamilyStudioManagement } from 'models/StudioManagement/studioManagement'
import React, {
    createRef,
    useCallback,
    useContext,
    useEffect,
    useRef,
} from 'react'
import { useParams } from 'react-router-dom'
import useGlobalSnackbar from 'shared/utils/useGlobalSnackbar'
import {
    CompleteMenuItemConfig,
    WorkspacePopover,
} from '../../components/WorkspacePopover'
import { useCustomTransition } from '../Common/hooks/useCustomTransition'
import { getUpdatedItemsArray } from '../WorkspaceContentArea'
import { WorkspaceContentContext } from '../WorkspaceContentContext'
import { ChangesCardContent } from './ChangesCardContent'
import { CommentSection } from './CommentSection'
import { TrackChangesApprovalTexts } from './enums/TrackChangesApprovalTexts'
import useBetaWorkspaceToggle from './hooks/useBetaWorkspaceToggle'
import { useCardHeightManager } from './hooks/useCardHeightManager'
import { useHandleShowMore } from './hooks/useHandleShowMore'

export interface ChangesCardStyleProps {
    isBetaWorkspaceToggleEnabled?: boolean
    isSelected?: boolean
    isHighlighted?: boolean
}

const useStyles = makeStyles<Theme, ChangesCardStyleProps>((theme: Theme) => ({
    cardButton: {
        padding: 0,
        color: (props) =>
            props.isBetaWorkspaceToggleEnabled &&
            (props.isSelected || props.isHighlighted)
                ? '#D3D2D3'
                : '#d3d3d3',
    },
    cardIconButton: {
        '&:hover': {
            color: '#0098D6',
            background: 'none',
        },
    },
    acceptIcon: {
        color: (props) =>
            props.isSelected || props.isHighlighted
                ? '#0D7B55 !important'
                : 'inherit',
    },
    rejectIcon: {
        color: (props) =>
            props.isSelected || props.isHighlighted
                ? '#B01111 !important'
                : 'inherit',
    },
    changesCard: {
        minWidth: pxToRem(234),
        margin: theme.spacing(1, 0),
        borderRadius: pxToRem(4),
        cursor: 'pointer',
        position: 'relative',
        zIndex: (props) => (props.isSelected || props.isHighlighted ? 10 : 1),
    },
    defaultCursor: {
        cursor: 'default',
    },
    cardContent: {
        minWidth: pxToRem(234),
        maxWidth: pxToRem(234),
        padding: theme.spacing(0, 2),
        marginBottom: theme.spacing(1),
        '&:last-child': {
            paddingBottom: 0,
        },
    },
    text: {
        wordBreak: 'break-word',
        fontSize: pxToRem(13),
        display: 'inline',
        fontWeight: 400,
        marginLeft: theme.spacing(0.625),
    },
    iconContainer: {
        paddingTop: theme.spacing(1),
    },
    acceptButtonContainer: {
        marginRight: theme.spacing(0.375),
    },
    highlight: {
        border: '1px solid rgba(0, 152, 214, 0.6)',
        boxShadow: '0px 1px 4px rgba(0, 152, 214, 0.65)',
    },
    commentSection: {
        borderTop: '1px solid #C9C9C9',
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
        '&:last-child': {
            paddingBottom: theme.spacing(2),
        },
    },
    comments: {
        borderTop: '1px solid #C9C9C9',
        padding: 0,
        '&:last-child': {
            paddingBottom: 0,
        },
    },
    showButton: {
        padding: 0,
        fontSize: pxToRem(13),
        color: '#866e51', //fix for wcag
        textDecoration: 'underline',
        width: '100%',
    },
}))

export interface ChangesCardProps {
    change: TrackedChange
    isSelected: boolean
    selectedTopic?: Partial<ProductFamilyStudioManagement>
    selectTracktip: (changeId: string | undefined) => void
    setCacheHeight?: (cardHeight: number, rowIndex: number) => void
    clearCacheRow?: (rowIndex: number) => void
    rowIndex?: number
    measure?: VoidFunction
    totalChanges?: number
}

export const ChangesCard = React.memo(
    ({
        change,
        isSelected,
        selectedTopic,
        selectTracktip,
        setCacheHeight,
        clearCacheRow,
        rowIndex,
        measure,
        totalChanges,
    }: ChangesCardProps) => {
        const isBetaWorkspaceToggleEnabled = useBetaWorkspaceToggle()

        const contentRef = useRef<any | undefined | null>()
        const actionRef = useRef<Array<HTMLButtonElement | null>>([])
        const [isHighlighted, setIsHighlighted] = React.useState<boolean>(false)
        const [
            isActionPopoverDisplayed,
            setIsActionPopoverDisplayed,
        ] = React.useState(false)
        const [
            isDeleteWarningDisplayed,
            setIsDeleteWarningDisplayed,
        ] = React.useState<boolean>(false)
        const { handleShowMoreContent } = useHandleShowMore()
        const [isCommentEditable, setIsCommentEditable] = React.useState<
            boolean
        >(false)
        const [
            popoverIndexReference,
            setPopoverAnchorIndexReference,
        ] = React.useState<number | undefined>()

        const [
            isShowMoreButtonDisplayed,
            setIsShowMoreButtonDisplayed,
        ] = React.useState(false)

        const {
            cardRef,
            handleHeightChange,
            handleShowMoreToggle,
        } = useCardHeightManager({
            setCacheHeight,
            clearCacheRow,
            rowIndex,
            measure,
            totalChanges,
        })

        const handleShowMore = useCallback(() => {
            setIsShowMoreButtonDisplayed((prev) => !prev)
            handleShowMoreToggle()
        }, [handleShowMoreToggle])

        const handleCommentHeightChange = useCallback(() => {
            handleHeightChange()
        }, [handleHeightChange])
        const classes = useStyles({
            isBetaWorkspaceToggleEnabled: isBetaWorkspaceToggleEnabled,
            isSelected: isSelected,
            isHighlighted: isHighlighted,
        })

        const { propertyToFocus } = useReactiveVar(workspaceProperty)
        const { selectedElementNode } = useReactiveVar(workspaceSelectedElement)
        const { currentUserInfo, isReviewer } = useReactiveVar(userInfo)
        const { projectId } = useParams<{ projectId?: string }>()

        const { handleRetrieveUpdatedDTV } = useContext(WorkspaceContentContext)
        const { showGlobalSnackbar } = useGlobalSnackbar()
        const [_, startTransition] = useCustomTransition()

        const [deleteComment] = useMutation<{
            input: DeleteTrackChangeCommentInput
        }>(DeleteTrackTipComment, {
            fetchPolicy: 'no-cache',
            onCompleted: () => {
                if (popoverIndexReference !== undefined) {
                    setTrackChangeComment(
                        comments[popoverIndexReference].trackChangeId,
                        comments.filter(
                            (_, index) => index !== popoverIndexReference
                        )
                    )
                }
                setPopoverAnchorIndexReference(undefined)
                setIsDeleteWarningDisplayed(false)
            },
            onError: () => {
                showGlobalSnackbar({
                    variables: {
                        message: `There was a problem deleting your comment.`,
                        variant: 'error',
                        id: 'CommentSection_AddCommentError',
                    },
                })
            },
        })

        const setUpdatedContentItemNodes = (
            newContentItemNodes: Partial<ContentItem>[]
        ) => {
            setWorkspaceContentItems(
                getUpdatedItemsArray(
                    workspaceContents()?.contentItemNodes?.filter(
                        (contentItem) => contentItem.id !== change.contentItemId
                    ) ?? [],
                    newContentItemNodes,
                    true,
                    false
                )
            )
        }

        const [getProjectProductFamily] = useLazyQuery<
            GetProjectProductFamilyResponse
        >(GetProjectProductFamilyContentItemNodes(6), {
            fetchPolicy: 'no-cache',
            onCompleted: (data) => {
                startTransition(() =>
                    setUpdatedContentItemNodes(
                        data?.getProjectProductFamily?.contentItemNodes ?? []
                    )
                )
            },
        })

        const [getSelectedNugget] = useLazyQuery<GetProjectNuggetResponse>(
            GetProjectNuggetContentItemNodes(6),
            {
                fetchPolicy: 'no-cache',
                displayName: 'GetProjectNugget',
                onCompleted: (data: GetProjectNuggetResponse) => {
                    startTransition(() =>
                        setUpdatedContentItemNodes(
                            data?.getProjectNugget?.contentItemNodes ?? []
                        )
                    )
                },
            }
        )

        const [acceptChange] = useMutation(AcceptTrackedChange, {
            fetchPolicy: 'no-cache',
            onCompleted: (data) => {
                const trackChanges = data?.acceptTrackChange as TrackedChange[]

                if (trackChanges.length === 0) {
                    showGlobalSnackbar({
                        variables: {
                            message: `There was a problem accepting the changes.`,
                            variant: 'error',
                            id: 'CommentSection_AcceptChangesError',
                        },
                    })
                    return
                }

                setElementTrackedChanges(trackChanges)
                const trackTip = trackChanges.find(
                    (trackTip) => trackTip.id === change.id
                )
                if (trackTip) {
                    updateContentItemNodes(trackTip)
                }
            },
            onError: () =>
                showGlobalSnackbar({
                    variables: {
                        message: `There was a problem accepting the changes.`,
                        variant: 'error',
                        id: 'CommentSection_AcceptChangesError',
                    },
                }),
        })

        const [rejectChange] = useMutation(RejectTrackedChange, {
            fetchPolicy: 'no-cache',
            onCompleted: (data) => {
                const trackChanges = data?.rejectTrackChange as TrackedChange[]

                if (trackChanges.length === 0) {
                    showGlobalSnackbar({
                        variables: {
                            message: `There was a problem rejecting the changes.`,
                            variant: 'error',
                            id: 'CommentSection_AcceptChangesError',
                        },
                    })
                    return
                }

                setElementTrackedChanges(trackChanges)
                const trackTip = trackChanges.find(
                    (trackTip) => trackTip.id === change.id
                )
                if (trackTip) {
                    updateContentItemNodes(trackTip)
                }
            },
            onError: () =>
                showGlobalSnackbar({
                    variables: {
                        message: `There was a problem rejecting the changes.`,
                        variant: 'error',
                        id: 'CommentSection_AcceptChangesError',
                    },
                }),
        })

        const comments = React.useMemo(() => change.trackChangeComments, [
            change.trackChangeComments,
        ])
        const isActionsAvailable = React.useMemo(
            () => change.status === TrackChangeStatus.PENDING,
            [change.status]
        )

        const triggerDelete = React.useCallback(() => {
            setIsDeleteWarningDisplayed(true)
            setIsActionPopoverDisplayed(false)
        }, [])

        const triggerEdit = React.useCallback(() => {
            setIsCommentEditable(true)
            setIsActionPopoverDisplayed(false)
        }, [])

        const cancelDelete = React.useCallback(() => {
            setIsDeleteWarningDisplayed(false)
            setPopoverAnchorIndexReference(undefined)
        }, [])

        const handleEdit = React.useCallback(() => {
            setIsCommentEditable(false)
            setIsActionPopoverDisplayed(false)
            setPopoverAnchorIndexReference(undefined)
        }, [])

        const cardMenuItems: CompleteMenuItemConfig[] = React.useMemo(
            () => [
                {
                    dataTestId: 'editComment',
                    label: 'Edit',
                    onClick: triggerEdit,
                },
                {
                    dataTestId: 'deleteComment',
                    label: 'Delete',
                    onClick: triggerDelete,
                },
            ],
            [triggerDelete, triggerEdit]
        )

        const handleTrackTipSelect = React.useCallback(() => {
            if (
                selectedElementNode &&
                selectedElementNode?.elementType !== 'ASSEMBLY'
            ) {
                const { tabSelected } = workspaceContents()
                const articleId = change?.rootContentItemId
                const contentId = change?.contentItemId
                const tabText =
                    tabSelected === 0
                        ? 'general'
                        : tabSelected === 1
                        ? 'products'
                        : 'executions'
                if (
                    articleId !== contentId ||
                    change?.partType?.toLowerCase() !== tabText
                ) {
                    setArticleToFocus(articleId)
                }
                if (
                    trackChangeTypeToChangedTexts?.length > 0 &&
                    change.status === TrackChangeStatus.OPTIONGROUP
                ) {
                    setOptionGroupToFocus(propertyOptionIdsArray)
                    setPropertyToFocus(propertyOptionIdsArray[0], contentId)
                } else {
                    setOptionGroupToFocus([])
                    setPropertyToFocus(change?.contentItemElementId, contentId)
                }
                setTabToFocus(change?.partType)
                setIsHighlighted(true)
            }
            selectTracktip(change?.id)
        }, [change, selectedElementNode, selectTracktip])

        const handleBlur = React.useCallback(() => {
            if (
                selectedElementNode &&
                selectedElementNode?.elementType !== 'ASSEMBLY' &&
                !isSelected
            ) {
                setIsHighlighted(false)
                resetOnFocusProperty()
            }
        }, [selectedElementNode, isSelected])

        const handleActionPopoverClick = React.useCallback(
            (indexReference: number) => {
                setIsActionPopoverDisplayed((prev) => !prev)
                setPopoverAnchorIndexReference(indexReference)
            },
            [isActionPopoverDisplayed]
        )

        const handleDeleteComment = React.useCallback(
            (commentIndex: number) => {
                deleteComment({
                    variables: {
                        input: {
                            id: comments[commentIndex].id,
                            trackChangeId: comments[commentIndex].trackChangeId,
                            projectId: projectId as string,
                        },
                    },
                })
            },
            [deleteComment, projectId, comments]
        )

        const handleAcceptChanges = React.useCallback(() => {
            startTransition(() =>
                acceptChange({
                    variables: {
                        input: {
                            trackChangeId: change.id,
                        },
                        includeProductTypeInFamilyTrackChanges:
                            selectedElementNode?.elementType ===
                            ProjectElementType.PRODUCTFAMILY,
                    },
                })
            )
        }, [change, acceptChange, selectedElementNode])

        const handleRejectChanges = React.useCallback(() => {
            startTransition(() =>
                rejectChange({
                    variables: {
                        input: {
                            trackChangeId: change.id,
                        },
                        includeProductTypeInFamilyTrackChanges:
                            selectedElementNode?.elementType ===
                            ProjectElementType.PRODUCTFAMILY,
                    },
                })
            )
        }, [change, rejectChange, selectedElementNode])

        const updateContentItemNodes = React.useCallback(
            (data: TrackedChange) => {
                if (
                    selectedElementNode?.elementType !== 'ASSEMBLY' ||
                    selectedTopic
                ) {
                    startTransition(() => handleRetrieveUpdatedDTV())

                    const queryInput = {
                        variables: {
                            partType: data.partType,
                            rootArticleContentItemIds: [data.rootContentItemId],
                            elementId: selectedTopic
                                ? selectedTopic.ppfId
                                : selectedElementNode?.elementId,
                            projectId: projectId as string,
                            productFamilyId:
                                data.elementType === 'PRODUCTTYPE'
                                    ? workspaceContents()
                                          ?.projectProductFamilyId
                                    : undefined,
                        },
                    }

                    switch (data.elementType) {
                        case ElementType.PRODUCTFAMILY:
                        case ElementType.SUSTAINABILITY:
                            getProjectProductFamily(queryInput)
                            break
                        case ElementType.PRODUCTTYPE:
                            getSelectedNugget(queryInput)
                            break
                        default:
                            break
                    }
                }
            },
            [
                getProjectProductFamily,
                getSelectedNugget,
                projectId,
                selectedElementNode,
            ]
        )
        useEffect(() => {
            if (cardRef?.current && setCacheHeight && rowIndex !== undefined) {
                // Use requestAnimationFrame to ensure DOM has been updated
                requestAnimationFrame(() => {
                    if (cardRef?.current) {
                        const height = cardRef.current.offsetHeight
                        if (height > 0) {
                            setCacheHeight(height, rowIndex)
                        }
                        // Call measure to notify CellMeasurer about height changes
                        if (measure) {
                            measure()
                        }
                    }
                })
            } else if (measure) {
                // Even if we don't have height cache functions, still call measure
                requestAnimationFrame(() => {
                    if (measure) {
                        measure()
                    }
                })
            }
        }, [
            setCacheHeight,
            rowIndex,
            isSelected,
            comments?.length,
            measure,
            isShowMoreButtonDisplayed,
        ])

        // Additional useEffect to measure height on initial mount
        useEffect(() => {
            if (cardRef?.current && setCacheHeight && rowIndex !== undefined) {
                // Small delay to ensure all content is rendered
                requestAnimationFrame(() => {
                    if (cardRef?.current) {
                        const height = cardRef.current.offsetHeight
                        if (height > 0) {
                            setCacheHeight(height, rowIndex)
                            if (measure) {
                                measure()
                            }
                        }
                    }
                })
            }
        }, []) // Only run on mount
        useEffect(() => {
            if (
                !!change?.contentItemElementId &&
                change?.contentItemElementId === propertyToFocus &&
                selectedElementNode?.elementType !== 'ASSEMBLY'
            ) {
                setIsHighlighted(true)
                if (cardRef) {
                    cardRef?.current?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                    })
                }
            }

            if (
                isHighlighted &&
                propertyToFocus &&
                propertyToFocus !== change?.contentItemElementId &&
                !propertyOptionIdsArray.some((item) => item === propertyToFocus)
            ) {
                setIsHighlighted(false)
            }

            if (
                selectedElementNode?.elementType !== 'ASSEMBLY' &&
                !propertyToFocus &&
                (isHighlighted || isSelected)
            ) {
                setIsHighlighted(false)
                selectTracktip(undefined)
            }
        }, [
            change,
            propertyToFocus,
            isHighlighted,
            selectedElementNode,
            isSelected,
            selectTracktip,
        ])

        useEffect(() => {
            return () => {
                if (cardRef) {
                    ;(cardRef as any).current = false
                }
                if (contentRef) {
                    contentRef.current = false
                }
            }
        }, [])

        useEffect(() => {
            if (actionRef.current.length !== comments?.length) {
                actionRef.current = Array(comments?.length).map(
                    (_, i) => actionRef.current[i] || createRef()
                ) as Array<HTMLButtonElement | null>
            }
        }, [comments, actionRef])

        const trackChangeTypeToChangedTexts = React.useMemo(() => {
            return change.trackChangeTypeToChangedTexts || []
        }, [change.trackChangeTypeToChangedTexts])

        const propertyOptionIdsArray = React.useMemo(() => {
            return trackChangeTypeToChangedTexts.map(
                (item) => item.contentItemElementId
            )
        }, [trackChangeTypeToChangedTexts])

        useEffect(() => {
            setIsShowMoreButtonDisplayed(
                trackChangeTypeToChangedTexts.length > 2
            )
        }, [trackChangeTypeToChangedTexts])

        const showMoreLabel = `Show ${
            isShowMoreButtonDisplayed ? 'More' : 'Less'
        }`
        const getAvatarLetter = React.useMemo(() => {
            return change?.userProfileInfo
                ? `${change?.userProfileInfo.firstName?.charAt(
                      0
                  )}${change?.userProfileInfo.lastName?.charAt(0)}`
                : ''
        }, [
            change?.userProfileInfo?.firstName,
            change?.userProfileInfo?.lastName,
        ])

        return (
            <>
                <Card
                    className={clsx(
                        classes.changesCard,
                        isHighlighted && classes.highlight,
                        isReviewer && classes.defaultCursor
                    )}
                    data-testid={`changeCard-${change?.id}`}
                    ref={cardRef}
                    onClick={handleTrackTipSelect}
                    onBlur={handleBlur}
                >
                    <ChangesCardContent
                        imageUrl={change?.userProfileInfo?.imageUrl as string}
                        avatarLetter={getAvatarLetter}
                        actionElement={
                            isActionsAvailable ? (
                                <GridContainer
                                    className={classes.iconContainer}
                                >
                                    <GridItem
                                        className={
                                            classes.acceptButtonContainer
                                        }
                                    >
                                        <HoverTooltip text={'Accept'}>
                                            <IconButton
                                                className={clsx(
                                                    classes.cardButton,
                                                    classes.cardIconButton
                                                )}
                                                data-testid="acceptButton"
                                                disableRipple
                                                disableFocusRipple
                                                onClick={handleAcceptChanges}
                                                disabled={isReviewer}
                                                aria-label="Accept"
                                            >
                                                <CheckIcon
                                                    className={clsx(
                                                        classes.cardIconButton,
                                                        isBetaWorkspaceToggleEnabled &&
                                                            classes.acceptIcon
                                                    )}
                                                    fontSize="small"
                                                />
                                            </IconButton>
                                        </HoverTooltip>
                                    </GridItem>
                                    <GridItem>
                                        <HoverTooltip text={'Reject'}>
                                            <IconButton
                                                className={clsx(
                                                    classes.cardButton,

                                                    classes.cardIconButton
                                                )}
                                                data-testid="closeButton"
                                                disableRipple
                                                disableFocusRipple
                                                onClick={handleRejectChanges}
                                                disabled={isReviewer}
                                                aria-label="Reject"
                                            >
                                                <CloseIcon
                                                    className={clsx(
                                                        classes.cardIconButton,
                                                        isBetaWorkspaceToggleEnabled &&
                                                            classes.rejectIcon
                                                    )}
                                                    fontSize="small"
                                                />
                                            </IconButton>
                                        </HoverTooltip>
                                    </GridItem>
                                </GridContainer>
                            ) : undefined
                        }
                        userName={`${change?.userProfileInfo?.firstName} ${change?.userProfileInfo?.lastName}`}
                        dateTime={change?.changeDateTime}
                        text={change?.changedText}
                        prefix={
                            ((change?.changeType?.toUpperCase() as keyof typeof TrackChangeType) !==
                                TrackChangeType.OPTIONGROUP &&
                                TrackChangeType[
                                    change?.changeType?.toUpperCase() as keyof typeof TrackChangeType
                                ]) ||
                            ''
                        }
                        isDeleteWarningDisplayed={false}
                        deleteCommentCallback={() => {}}
                        isEditable={false}
                        isSelected={isSelected || isHighlighted}
                        onHeightChange={handleCommentHeightChange}
                    />

                    {trackChangeTypeToChangedTexts.length > 0 &&
                        trackChangeTypeToChangedTexts
                            .slice(
                                0,
                                isShowMoreButtonDisplayed
                                    ? 2
                                    : trackChangeTypeToChangedTexts.length
                            )
                            .map((changedText) => (
                                <ChangesCardContent
                                    imageUrl={
                                        change?.acceptedRejectedProfileInfo
                                            ?.imageUrl as string
                                    }
                                    avatarLetter={`${change?.acceptedRejectedProfileInfo?.firstName?.charAt(
                                        0
                                    )}${change?.acceptedRejectedProfileInfo?.lastName?.charAt(
                                        0
                                    )}`}
                                    userName={`${change?.acceptedRejectedProfileInfo?.firstName} ${change?.acceptedRejectedProfileInfo?.lastName}`}
                                    dateTime={
                                        change?.approvedOrRejectedDateTime
                                    }
                                    prefix={
                                        ((changedText?.trackChangeType?.toUpperCase() as keyof typeof TrackChangeType) !==
                                            TrackChangeType.OPTIONGROUP &&
                                            TrackChangeType[
                                                changedText?.trackChangeType?.toUpperCase() as keyof typeof TrackChangeType
                                            ]) ||
                                        ''
                                    }
                                    text={changedText.changedText}
                                    isDeleteWarningDisplayed={false}
                                    isSelected={isSelected || isHighlighted}
                                    changeStatus={change.status}
                                    hideHeader={true}
                                    contentItemElementId={
                                        change.contentItemElementId
                                    }
                                    onHeightChange={handleCommentHeightChange}
                                />
                            ))}
                    {trackChangeTypeToChangedTexts.length > 2 && (
                        <Button
                            data-testid="show-more-btn"
                            onClick={handleShowMore}
                            className={classes.showButton}
                            disableRipple
                            disableFocusRipple
                            aria-label={showMoreLabel}
                        >
                            {showMoreLabel}
                        </Button>
                    )}
                    {comments?.map((comment, index) => {
                        return (
                            <React.Fragment key={comment.id}>
                                <CardContent className={classes.comments}>
                                    <ChangesCardContent
                                        imageUrl={
                                            comment.userProfileInfo
                                                ?.imageUrl as string
                                        }
                                        avatarLetter={`${comment.userProfileInfo?.firstName?.charAt(
                                            0
                                        )}${comment.userProfileInfo?.lastName?.charAt(
                                            0
                                        )}`}
                                        actionElement={
                                            currentUserInfo?.kpUserId ===
                                            comment.userId ? (
                                                <IconButton
                                                    className={clsx(
                                                        classes.cardButton,
                                                        classes.cardIconButton
                                                    )}
                                                    disableRipple
                                                    disableFocusRipple
                                                    ref={(el) =>
                                                        (actionRef.current[
                                                            index
                                                        ] = el)
                                                    }
                                                    onClick={() =>
                                                        handleActionPopoverClick(
                                                            index
                                                        )
                                                    }
                                                    data-testid="commentActionButton"
                                                    aria-label="More Actions"
                                                >
                                                    <MoreVertIcon fontSize="small" />
                                                </IconButton>
                                            ) : (
                                                <></>
                                            )
                                        }
                                        userName={`${comment.userProfileInfo?.firstName} ${comment.userProfileInfo?.lastName}`}
                                        dateTime={
                                            comment.updatedDate ??
                                            comment.createdDate
                                        }
                                        text={`${comment.comment}`}
                                        isDeleteWarningDisplayed={
                                            popoverIndexReference === index &&
                                            isDeleteWarningDisplayed
                                        }
                                        hideDeleteWarning={cancelDelete}
                                        deleteCommentCallback={
                                            handleDeleteComment
                                        }
                                        indexReference={index}
                                        isEditable={
                                            popoverIndexReference === index &&
                                            isCommentEditable
                                        }
                                        editableProps={{
                                            trackChangeId:
                                                comment.trackChangeId,
                                            commentId: comment.id,
                                            handleEdit: handleEdit,
                                            onHeightChange: handleCommentHeightChange,
                                        }}
                                        isSelected={isSelected || isHighlighted}
                                    />
                                </CardContent>

                                {popoverIndexReference === index &&
                                    isActionPopoverDisplayed && (
                                        <WorkspacePopover
                                            menuItems={cardMenuItems}
                                            anchorElement={
                                                actionRef.current[
                                                    index
                                                ] as Element
                                            }
                                            isActionPopoverDisplayed={
                                                popoverIndexReference ===
                                                    index &&
                                                isActionPopoverDisplayed
                                            }
                                            handleActionPopoverClick={() =>
                                                handleActionPopoverClick(index)
                                            }
                                            anchorOrigin={{
                                                vertical:
                                                    ((actionRef.current[
                                                        index
                                                    ] as any)?.clientHeight ??
                                                        0) + 78,
                                                horizontal:
                                                    ((actionRef.current[
                                                        index
                                                    ] as any)?.clientWidth ??
                                                        0) - 9,
                                            }}
                                        />
                                    )}
                            </React.Fragment>
                        )
                    })}
                    {change.status !== TrackChangeStatus.PENDING &&
                        change.status !== TrackChangeStatus.OPTIONGROUP && (
                            <ChangesCardContent
                                imageUrl={
                                    change?.acceptedRejectedProfileInfo
                                        ?.imageUrl as string
                                }
                                avatarLetter={`${change?.acceptedRejectedProfileInfo?.firstName?.charAt(
                                    0
                                )}${change?.acceptedRejectedProfileInfo?.lastName?.charAt(
                                    0
                                )}`}
                                userName={`${change?.acceptedRejectedProfileInfo?.firstName} ${change?.acceptedRejectedProfileInfo?.lastName}`}
                                dateTime={change?.approvedOrRejectedDateTime}
                                text={
                                    change.status === TrackChangeStatus.APPROVED
                                        ? TrackChangesApprovalTexts.Accepted
                                        : TrackChangesApprovalTexts.Rejected
                                }
                                isDeleteWarningDisplayed={false}
                                isSelected={isSelected || isHighlighted}
                                changeStatus={change.status}
                                onHeightChange={handleCommentHeightChange}
                            />
                        )}
                    {(isSelected || isHighlighted) && (
                        <CardContent className={classes.commentSection}>
                            <CommentSection
                                key={`comment-${
                                    change?.trackChangeComments?.[
                                        change?.trackChangeComments.length - 1
                                    ]?.id
                                }`}
                                trackChangeId={change.id}
                                elementId={change.elementId}
                                familyId={change.familyId}
                                onHeightChange={handleCommentHeightChange}
                            />
                        </CardContent>
                    )}
                </Card>
            </>
        )
    },
    (prevProps, nextProps) => {
        // Check if comment content has changed (not just length)
        const prevComments = prevProps.change.trackChangeComments || []
        const nextComments = nextProps.change.trackChangeComments || []

        const commentsEqual =
            prevComments.length === nextComments.length &&
            prevComments.every((prevComment, index) => {
                const nextComment = nextComments[index]
                return (
                    prevComment?.id === nextComment?.id &&
                    prevComment?.comment === nextComment?.comment &&
                    prevComment?.updatedDate === nextComment?.updatedDate
                )
            })

        // Check if trackChangeTypeToChangedTexts has changed
        const prevTrackChangeTexts =
            prevProps.change.trackChangeTypeToChangedTexts || []
        const nextTrackChangeTexts =
            nextProps.change.trackChangeTypeToChangedTexts || []

        const trackChangeTextsEqual =
            prevTrackChangeTexts.length === nextTrackChangeTexts.length &&
            prevTrackChangeTexts.every((prevText, index) => {
                const nextText = nextTrackChangeTexts[index]
                return (
                    prevText?.changedText === nextText?.changedText &&
                    prevText?.contentItemElementId ===
                        nextText?.contentItemElementId &&
                    prevText?.trackChangeType === nextText?.trackChangeType &&
                    prevText?.authoredUserId === nextText?.authoredUserId
                )
            })

        return (
            prevProps.change.id === nextProps.change.id &&
            prevProps.isSelected === nextProps.isSelected &&
            prevProps.selectedTopic?.ppfId === nextProps.selectedTopic?.ppfId &&
            prevProps.change.status === nextProps.change.status &&
            commentsEqual &&
            trackChangeTextsEqual &&
            prevProps.rowIndex === nextProps.rowIndex &&
            prevProps.measure === nextProps.measure &&
            prevProps.clearCacheRow === nextProps.clearCacheRow
        )
    }
)
